import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, RefreshCcw } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ExcelJS from "exceljs";
import api from "../services/api";

const parseEsmpList = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.esmps)) return payload.esmps;
  return [];
};

const reportStatus = (esmp) => {
  if (esmp?.status === "Approved") return "Approved";

  const deadline = esmp?.deadline ? new Date(esmp.deadline) : null;
  const deadlinePassed = deadline && !Number.isNaN(deadline.getTime()) && deadline < new Date();
  if (esmp?.status === "Overdue" || deadlinePassed) return "Overdue";

  return "Pending";
};

const statusClasses = {
  Pending: "bg-amber-100 text-amber-800",
  Approved: "bg-green-100 text-green-800",
  Overdue: "bg-red-100 text-red-800",
};

export default function StatisticsReport({ Sidebar }) {
  const [esmps, setEsmps] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get("/esmp/list");
      setEsmps(parseEsmpList(response));
    } catch (requestError) {
      console.error("Failed to load Statistics & Reports:", requestError);
      setError(requestError.response?.data?.message || "Failed to load Statistics & Reports");
      setEsmps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const reportRows = useMemo(
    () =>
      esmps.map((esmp) => ({
        ...esmp,
        report_status: reportStatus(esmp),
      })),
    [esmps],
  );

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return reportRows;

    return reportRows.filter((esmp) =>
      [
        esmp.esmp_id,
        esmp.district,
        esmp.sector,
        esmp.risk_rating,
        esmp.cycle,
        esmp.report_status,
      ].some((value) => String(value || "").toLowerCase().includes(query)),
    );
  }, [reportRows, search]);

  const totals = useMemo(
    () =>
      reportRows.reduce(
        (counts, esmp) => ({
          ...counts,
          [esmp.report_status]: counts[esmp.report_status] + 1,
        }),
        { Pending: 0, Approved: 0, Overdue: 0 },
      ),
    [reportRows],
  );

  const districtChartData = useMemo(() => {
    const districts = new Map();

    reportRows.forEach((esmp) => {
      const district = esmp.district || "Unspecified";
      const current = districts.get(district) || {
        district,
        Pending: 0,
        Approved: 0,
        Overdue: 0,
      };
      current[esmp.report_status] += 1;
      districts.set(district, current);
    });

    return Array.from(districts.values()).sort(
      (a, b) =>
        b.Overdue - a.Overdue ||
        b.Pending - a.Pending ||
        a.district.localeCompare(b.district),
    );
  }, [reportRows]);

  const exportToExcel = async () => {
    try {
      setExporting(true);

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "VC ESMP Tracker";
      workbook.created = new Date();

      const reportSheet = workbook.addWorksheet("ESMP Report", {
        views: [{ state: "frozen", ySplit: 1 }],
      });
      reportSheet.columns = [
        { header: "ESMP Code", key: "esmpCode", width: 22 },
        { header: "District", key: "district", width: 20 },
        { header: "Sector", key: "sector", width: 22 },
        { header: "Risk Rating", key: "riskRating", width: 18 },
        { header: "Cycle", key: "cycle", width: 16 },
        { header: "Status", key: "status", width: 16 },
      ];

      filteredRows.forEach((esmp) => {
        reportSheet.addRow({
          esmpCode: esmp.esmp_id || "N/A",
          district: esmp.district || "N/A",
          sector: esmp.sector || "N/A",
          riskRating: esmp.risk_rating || "N/A",
          cycle: esmp.cycle || "N/A",
          status: esmp.report_status,
        });
      });

      const headerRow = reportSheet.getRow(1);
      headerRow.height = 24;
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF15803D" },
      };
      headerRow.alignment = { vertical: "middle" };
      reportSheet.autoFilter = {
        from: "A1",
        to: `F${Math.max(reportSheet.rowCount, 1)}`,
      };

      reportSheet.eachRow((row, rowNumber) => {
        row.alignment = { vertical: "middle" };
        if (rowNumber > 1) {
          row.height = 20;
          const statusCell = row.getCell(6);
          const colors = {
            Pending: "FFFFF3CD",
            Approved: "FFD1E7DD",
            Overdue: "FFF8D7DA",
          };
          statusCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: colors[statusCell.value] || "FFFFFFFF" },
          };
        }
        row.eachCell((cell) => {
          cell.border = {
            bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
          };
        });
      });

      const summarySheet = workbook.addWorksheet("Summary");
      summarySheet.columns = [
        { header: "Status", key: "status", width: 20 },
        { header: "ESMP Count", key: "count", width: 16 },
      ];
      summarySheet.addRows([
        { status: "Total ESMPs", count: filteredRows.length },
        { status: "Pending", count: filteredRows.filter((row) => row.report_status === "Pending").length },
        { status: "Approved", count: filteredRows.filter((row) => row.report_status === "Approved").length },
        { status: "Overdue", count: filteredRows.filter((row) => row.report_status === "Overdue").length },
      ]);
      summarySheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      summarySheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF15803D" },
      };
      summarySheet.getColumn(2).numFmt = "#,##0";

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ESMP-Statistics-Report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (exportError) {
      console.error("Failed to export report:", exportError);
      setError("Failed to generate the Excel workbook");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      <main className="flex-1 p-6 space-y-6">
        <div className="flex flex-col gap-4 rounded-xl bg-gradient-to-r from-green-700 to-green-500 p-5 text-white shadow-md md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-2xl font-bold">Statistics & Reports</h2>
            <p className="mt-1 text-sm text-green-50">ESMP status report by district, sector, risk and cycle</p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              type="search"
              placeholder="Search report..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="rounded-lg border px-3 py-2 text-black outline-none focus:ring-2 focus:ring-green-200"
            />
            <button
              type="button"
              onClick={loadReport}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 font-semibold text-green-700 hover:bg-green-50"
            >
              <RefreshCcw size={16} />
              Refresh
            </button>
            <button
              type="button"
              onClick={exportToExcel}
              disabled={exporting || filteredRows.length === 0}
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 font-semibold ${
                exporting || filteredRows.length === 0
                  ? "cursor-not-allowed bg-gray-200 text-gray-500"
                  : "bg-emerald-900 text-white hover:bg-emerald-950"
              }`}
            >
              <Download size={16} />
              {exporting ? "Generating..." : "Export to Excel"}
            </button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl border-l-4 border-blue-600 bg-white p-4 shadow">
            <p className="text-xs text-gray-500">Total ESMPs</p>
            <p className="text-2xl font-bold text-blue-700">{reportRows.length}</p>
          </div>
          <div className="rounded-xl border-l-4 border-amber-500 bg-white p-4 shadow">
            <p className="text-xs text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-amber-700">{totals.Pending}</p>
          </div>
          <div className="rounded-xl border-l-4 border-green-600 bg-white p-4 shadow">
            <p className="text-xs text-gray-500">Approved</p>
            <p className="text-2xl font-bold text-green-700">{totals.Approved}</p>
          </div>
          <div className="rounded-xl border-l-4 border-red-600 bg-white p-4 shadow">
            <p className="text-xs text-gray-500">Overdue</p>
            <p className="text-2xl font-bold text-red-700">{totals.Overdue}</p>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-xl bg-white shadow">
          {loading ? (
            <div className="py-12 text-center">
              <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-green-700" />
              <p className="mt-4 text-gray-500">Loading report...</p>
            </div>
          ) : filteredRows.length === 0 ? (
            <p className="py-12 text-center text-gray-500">No ESMP records found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-green-50 text-green-800">
                    <th className="border px-4 py-3 text-left">ESMP Code</th>
                    <th className="border px-4 py-3 text-left">District</th>
                    <th className="border px-4 py-3 text-left">Sector</th>
                    <th className="border px-4 py-3 text-left">Risk Rating</th>
                    <th className="border px-4 py-3 text-left">Cycle</th>
                    <th className="border px-4 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((esmp) => (
                    <tr key={esmp.id} className="hover:bg-green-50">
                      <td className="border px-4 py-3 font-semibold text-blue-700">
                        {esmp.esmp_id || "N/A"}
                      </td>
                      <td className="border px-4 py-3">{esmp.district || "N/A"}</td>
                      <td className="border px-4 py-3">{esmp.sector || "N/A"}</td>
                      <td className="border px-4 py-3">{esmp.risk_rating || "N/A"}</td>
                      <td className="border px-4 py-3">{esmp.cycle || "N/A"}</td>
                      <td className="border px-4 py-3">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClasses[esmp.report_status]}`}>
                          {esmp.report_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-xl bg-white p-6 shadow">
          <div className="mb-5">
            <h3 className="text-lg font-semibold text-green-700">ESMP Status by District</h3>
            <p className="mt-1 text-sm text-gray-500">
              Compare pending, approved and overdue ESMPs across districts.
            </p>
          </div>

          {districtChartData.length === 0 ? (
            <p className="py-10 text-center text-gray-500">No chart data available.</p>
          ) : (
            <div className="w-full overflow-x-auto">
              <div style={{ minWidth: Math.max(760, districtChartData.length * 72) }}>
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart
                    data={districtChartData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 75 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="district"
                      angle={-40}
                      textAnchor="end"
                      interval={0}
                      height={95}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend verticalAlign="top" height={36} />
                    <Bar dataKey="Pending" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Approved" fill="#16a34a" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Overdue" fill="#dc2626" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
